import { fetchMediaUrl, isApiUrl } from '@/lib/media';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/** Resolve an API-backed media URL into a signed S3 URL reactively.
 *  Returns `undefined` while loading or when `url` is empty. Non-API URLs
 *  pass through unchanged on the first render. Mirrors the resolution
 *  behavior that the legacy `ContactAvatar` components bundled — extracted
 *  so call sites of the DS `Avatar` (which expects a resolved `src`) can
 *  opt in without re-implementing the async dance. */
export const useResolvedMediaUrl = (url: string | null | undefined): string | undefined => {
  const queryClient = useQueryClient();
  const [resolved, setResolved] = useState<string | undefined>(() =>
    url && !isApiUrl(url) ? url : undefined,
  );

  useEffect(() => {
    if (!url) {
      setResolved(undefined);
      return;
    }
    if (!isApiUrl(url)) {
      setResolved(url);
      return;
    }
    let cancelled = false;
    queryClient
      .fetchQuery({
        // Signed API URLs typically expire in ~1h. Stay a few minutes inside
        // that window so we don't serve an expired signature to <img>.
        queryKey: ['media-item', url],
        queryFn: () => fetchMediaUrl(url),
        staleTime: 50 * 60_000,
        gcTime: 60 * 60_000,
      })
      .then((signed) => {
        if (!cancelled && signed) {
          setResolved(signed);
        }
      })
      .catch(() => {
        // Swallow — caller falls back to initial/unknown when resolved is undefined.
      });
    return () => {
      cancelled = true;
    };
  }, [url, queryClient]);

  return resolved;
};
